import React, { useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Text, useTexture } from '@react-three/drei';
import * as THREE from 'three';

// A gallery-style double glass door: slim black steel frames, clear glass, long brass pull handles
// and a brass name plaque above. Built in local space facing +z (the visitor stands at +z) and it
// opens away from the visitor (towards -z), where a softly lit marble passage is visible.

export const DOOR = { width: 2.6, height: 3.1 }; // clear opening
const LEAF_W = DOOR.width / 2;
const STEEL = { color: '#141416', metalness: 0.75, roughness: 0.35 };
const BRASS = { color: '#b8913d', metalness: 1, roughness: 0.22 };
const FONT = '/fonts/Bliamer.otf';

const Leaf = ({ side, glassMaterial }) => {
  // side = -1 (left leaf, hinge on the left) | 1 (right leaf, hinge on the right)
  const w = LEAF_W - 0.01;
  const h = DOOR.height - 0.02;
  const stile = 0.06;
  return (
    // Leaf geometry extends from its hinge towards the centre of the opening.
    <group position={[(-side * w) / 2, h / 2, 0]}>
      {/* steel frame: two stiles, top rail, kick plate */}
      {[-1, 1].map((s) => (
        <mesh key={s} position={[(s * (w - stile)) / 2, 0, 0]} castShadow>
          <boxGeometry args={[stile, h, 0.05]} />
          <meshStandardMaterial {...STEEL} />
        </mesh>
      ))}
      <mesh position={[0, h / 2 - 0.04, 0]}>
        <boxGeometry args={[w, 0.08, 0.05]} />
        <meshStandardMaterial {...STEEL} />
      </mesh>
      <mesh position={[0, -h / 2 + 0.11, 0]}>
        <boxGeometry args={[w, 0.22, 0.05]} />
        <meshStandardMaterial {...STEEL} />
      </mesh>
      {/* glass */}
      <mesh position={[0, 0.07, 0]} material={glassMaterial}>
        <boxGeometry args={[w - stile * 2, h - 0.3, 0.012]} />
      </mesh>
      {/* brass pull handles on both faces, near the meeting edge */}
      {[0.09, -0.09].map((z) => (
        <group key={z} position={[(-side * (w / 2 - 0.16)), 0, z]}>
          <mesh>
            <cylinderGeometry args={[0.018, 0.018, 1.1, 16]} />
            <meshStandardMaterial {...BRASS} />
          </mesh>
          {[-0.45, 0.45].map((y) => (
            <mesh key={y} position={[0, y, -Math.sign(z) * 0.04]} rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[0.011, 0.011, 0.08, 10]} />
              <meshStandardMaterial {...BRASS} />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  );
};

// Warm marble passage behind the door, so the glass shows somewhere to go.
const Passage = ({ depth = 5.2 }) => {
  const [diff, normal] = useTexture(['/salon/FloorsCheckerboard_S_Diffuse.jpg', '/salon/FloorsCheckerboard_S_Normal.jpg']);
  useMemo(() => {
    [diff, normal].forEach((t) => { t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(2, 3); });
    diff.colorSpace = THREE.SRGBColorSpace;
  }, [diff, normal]);
  const w = DOOR.width + 0.8;
  const h = DOOR.height + 0.5;
  const wall = { color: '#efe6d6', emissive: '#4a3b28', emissiveIntensity: 0.35, roughness: 0.9 };
  return (
    <group position={[0, 0, -depth / 2 - 0.15]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.005, 0]}>
        <planeGeometry args={[w, depth]} />
        <meshStandardMaterial map={diff} normalMap={normal} roughness={0.25} emissive="#2a2016" emissiveIntensity={0.4} />
      </mesh>
      {[-1, 1].map((s) => (
        <mesh key={s} position={[(s * w) / 2, h / 2, 0]} rotation={[0, -s * Math.PI / 2, 0]}>
          <planeGeometry args={[depth, h]} />
          <meshStandardMaterial {...wall} />
        </mesh>
      ))}
      {/* far wall with a bright arched opening glow */}
      <mesh position={[0, h / 2, -depth / 2]}>
        <planeGeometry args={[w, h]} />
        <meshStandardMaterial {...wall} />
      </mesh>
      <mesh position={[0, 1.4, -depth / 2 + 0.01]}>
        <planeGeometry args={[1.4, 2.6]} />
        <meshBasicMaterial color="#fff1d6" toneMapped={false} />
      </mesh>
      {/* ceiling with a warm light panel */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, h, 0]}>
        <planeGeometry args={[w, depth]} />
        <meshStandardMaterial color="#f4ece0" emissive="#fff0d8" emissiveIntensity={0.25} />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, h - 0.01, 0]}>
        <planeGeometry args={[0.9, depth * 0.7]} />
        <meshBasicMaterial color="#fff4e0" toneMapped={false} />
      </mesh>
    </group>
  );
};

export const GlassDoor = ({ open = false, onEnter, onHover, onHoverOut, label = 'The Grand Salon', transmission = true, ...props }) => {
  const leftRef = useRef();
  const rightRef = useRef();
  const [hovered, setHovered] = useState(false);
  const { gl } = useThree();

  const glassMaterial = useMemo(
    () =>
      new THREE.MeshPhysicalMaterial(
        transmission
          ? { color: '#f2f8f8', transmission: 1, roughness: 0.04, thickness: 0.02, ior: 1.5, metalness: 0, envMapIntensity: 1.2 }
          : { color: '#cfe0e2', transparent: true, opacity: 0.22, roughness: 0.03, metalness: 0, envMapIntensity: 1.5, depthWrite: false }
      ),
    [transmission]
  );

  useFrame((_, delta) => {
    const target = open ? 1.35 : 0;
    const k = Math.min(1, delta * 2.2);
    if (leftRef.current) leftRef.current.rotation.y += (target - leftRef.current.rotation.y) * k;
    if (rightRef.current) rightRef.current.rotation.y += (-target - rightRef.current.rotation.y) * k;
  });

  const handlers = {
    onClick: (e) => { e.stopPropagation(); onEnter?.(); },
    onPointerOver: (e) => { e.stopPropagation(); setHovered(true); gl.domElement.style.cursor = 'pointer'; onHover?.(); },
    onPointerOut: () => { setHovered(false); gl.domElement.style.cursor = 'grab'; onHoverOut?.(); },
  };

  const frameW = 0.12;
  return (
    <group {...props}>
      {/* steel door surround */}
      {[-1, 1].map((s) => (
        <mesh key={s} position={[s * (DOOR.width / 2 + frameW / 2), DOOR.height / 2 + 0.06, 0]}>
          <boxGeometry args={[frameW, DOOR.height + 0.12, 0.2]} />
          <meshStandardMaterial {...STEEL} />
        </mesh>
      ))}
      <mesh position={[0, DOOR.height + frameW / 2, 0]}>
        <boxGeometry args={[DOOR.width + frameW * 2, frameW, 0.2]} />
        <meshStandardMaterial {...STEEL} />
      </mesh>

      {/* leaves, hinged at the outer edges */}
      <group ref={leftRef} position={[-DOOR.width / 2, 0, 0]}>
        <Leaf side={-1} glassMaterial={glassMaterial} />
      </group>
      <group ref={rightRef} position={[DOOR.width / 2, 0, 0]}>
        <Leaf side={1} glassMaterial={glassMaterial} />
      </group>

      {/* invisible click/hover target covering the doorway */}
      <mesh position={[0, DOOR.height / 2, 0.12]} {...handlers}>
        <planeGeometry args={[DOOR.width, DOOR.height]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* brass plaque */}
      <group position={[0, DOOR.height + 0.5, 0.06]}>
        <mesh>
          <boxGeometry args={[2.2, 0.42, 0.04]} />
          <meshStandardMaterial {...STEEL} />
        </mesh>
        <Text position={[0, 0, 0.025]} fontSize={0.26} font={FONT} color={hovered ? '#ffe39a' : '#d4ad55'} anchorX="center" anchorY="middle">
          {label}
        </Text>
      </group>

      <Passage />
    </group>
  );
};

export default GlassDoor;
